import { ExportMenu } from "../components/ExportMenu";

function money(amount, currency = "usd") {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: String(currency || "usd").toUpperCase()
  }).format(Number(amount || 0) / 100);
}

function lineType(receipt) {
  const type = String(receipt.entityType || receipt.rangegridEntityType || receipt.metadata?.rangegridEntityType || "").toLowerCase();
  if (type.includes("membership")) {
    return "Membership";
  }
  if (type.includes("reservation")) {
    return "Reservations";
  }
  if (type.includes("event")) {
    return "Events";
  }
  if (type.includes("donation")) {
    return "Donations";
  }
  return "Other";
}

export function AccountingPage({
  donations = [],
  loading,
  onExportDonations,
  onExportEvents,
  onExportPaymentEvents,
  onExportPaymentReconciliation,
  onExportPaymentReceipts,
  onExportReservations,
  paymentEvents,
  paymentReceipts,
  reservations = []
}) {
  const receipts = paymentReceipts?.receipts || [];
  const events = paymentEvents?.events || [];
  const paidReceipts = receipts.filter((receipt) => !["void", "refunded"].includes(receipt.status));
  const gross = paidReceipts.reduce((total, receipt) => total + Number(receipt.amount || receipt.total || 0), 0);
  const refunds = receipts.reduce((total, receipt) => total + Number(receipt.refund?.amount || (receipt.status === "refunded" ? receipt.amount : 0) || 0), 0);
  const pending = receipts.filter((receipt) => ["open", "pending", "unpaid"].includes(receipt.status)).length;
  const byLine = ["Membership", "Reservations", "Events", "Donations", "Other"].map((label) => {
    const lineReceipts = receipts.filter((receipt) => lineType(receipt) === label);
    return {
      label,
      count: lineReceipts.length,
      amount: lineReceipts.reduce((total, receipt) => total + Number(receipt.amount || receipt.total || 0), 0)
    };
  });

  return (
    <div className="reports-page">
      {loading && <div className="status-strip">Loading accounting data...</div>}
      <section className="module-metrics">
        <div>
          <span>Gross receipts</span>
          <strong>{money(gross)}</strong>
        </div>
        <div>
          <span>Refunds</span>
          <strong>{money(refunds)}</strong>
        </div>
        <div>
          <span>Pending receivables</span>
          <strong>{pending}</strong>
        </div>
        <div>
          <span>Payment events</span>
          <strong>{events.length}</strong>
        </div>
      </section>

      <div className="report-grid">
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Reconciliation Lines</h2>
              <p>Accounting view grouped by product surface, ready to compare against payment processor exports.</p>
            </div>
          </div>
          <div className="table-like compact-table">
            <div className="table-row table-head">
              <span>Line</span>
              <span>Count</span>
              <span>Amount</span>
            </div>
            {byLine.map((line) => (
              <div className="table-row" key={line.label}>
                <span>{line.label}</span>
                <span>{line.count}</span>
                <span>{money(line.amount)}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Reconciliation checks</h2>
              <p>Counts that should reconcile to receipts, refunds, and exports.</p>
            </div>
          </div>
          <dl className="ops-facts">
            <div>
              <dt>Reservation records</dt>
              <dd>{reservations.length}</dd>
            </div>
            <div>
              <dt>Donation records</dt>
              <dd>{donations.length}</dd>
            </div>
            <div>
              <dt>Refund-related events</dt>
              <dd>{events.filter((event) => String(event.type || event.eventType || "").includes("refund")).length}</dd>
            </div>
          </dl>
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Export accounting data</h2>
              <p>Choose one ledger at a time so the screen stays simple and totals remain traceable.</p>
            </div>
          </div>
          <ExportMenu
            buttonLabel="Export accounting data"
            items={[
              {
                key: "payment-reconciliation",
                label: "Payment reconciliation",
                description: "Receipt ledger with gross, refund, net, and payout-review status.",
                onExport: onExportPaymentReconciliation
              },
              {
                key: "receipts",
                label: "Receipts",
                description: "Durable payment receipts for reconciliation and support.",
                onExport: onExportPaymentReceipts
              },
              {
                key: "payment-events",
                label: "Payment events",
                description: "Payment activity ledger for audit review.",
                onExport: onExportPaymentEvents
              },
              {
                key: "reservations",
                label: "Reservations",
                description: "Reservation source records tied to lane or bay revenue.",
                onExport: onExportReservations
              },
              {
                key: "events",
                label: "Events",
                description: "Event setup records tied to registration revenue.",
                onExport: onExportEvents
              },
              {
                key: "donations",
                label: "Donations",
                description: "Donation records and refund/reconciliation status.",
                onExport: onExportDonations
              }
            ]}
            title="Choose accounting export"
          />
        </section>
      </div>
    </div>
  );
}
